import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { safeStorage } from '../utils/safeStorage';
import {
  Settings, Shield, LogOut, ChevronLeft, ChevronRight, ChevronDown, LayoutDashboard, Menu, X, User, ShoppingCart,
  Phone, Mail, Megaphone, Upload, Webhook, Sparkles, Activity, HardDrive, Users2, Receipt, Tag,
  FolderKanban, Heart, Wallet, FileBarChart2, BookOpen, Building2, CalendarDays, ClipboardList, FileSearch,
  Grid3x3, Table2, BarChart3, FileUp, FileText, Calendar, Handshake, GraduationCap,
  Clock, Bus, Compass, TrendingUp, Leaf, Calculator, Banknote, ArrowRightLeft, Globe, Award, Rocket, Ship, Boxes,
  Gift, DollarSign, HardHat, Scale, Briefcase, UtensilsCrossed, FolderHeart, Plus, ExternalLink, Printer, RefreshCw, Landmark, Target, Crown, Copy, Cloud, Send, Zap, Bell, MessageSquare, CalendarCheck, PhoneCall, GitBranch, AlertTriangle, Star, UserPlus, Trophy, Truck, ShieldAlert, Monitor, Armchair, ClipboardCheck, Route, FileSignature, Lightbulb, ShieldCheck, AlertOctagon, Layout, MessageCircleHeart, Download, Crosshair, GitPullRequest, FolderOpen, RotateCcw, Coffee, Settings2, Network, Gauge, Kanban, Flag, Car, DoorOpen, UserMinus, Database, Headphones, BadgeCheck, Newspaper, Projector, Palmtree, Plane, Key, Package, SprayCan, Stamp, FileCheck, CalendarClock, Wrench, Lock,
  Building, PenTool, CreditCard, Archive, ListChecks, MessageSquareWarning, Laptop, Gavel, ScrollText, PackageX, HandCoins, SmilePlus, PieChart, PartyPopper, ParkingCircle, KeyRound, DatabaseBackup, Medal, TreePine, FlaskConical,
  CalendarOff, Palette, UserCheck, MessageCircle, FileSpreadsheet, LayoutTemplate, ShoppingBag, FileCheck2, PackageCheck, Percent, Users, Gem, Ticket, BookText, ClipboardPen, LayoutGrid, Filter, Layers, ContactRound, KeySquare, Share2, Warehouse, BookUser, Vote, Milestone, Fuel, ThumbsUp, ArchiveRestore, Siren, Timer, Brain, Recycle, Magnet, Maximize, LineChart, Store, UserCircle, Flame, Bug, Play, HeartPulse, Quote, FileWarning, Smile, Grid2X2, UserSquare, FileBarChart, Vault, Search, Thermometer, MapPin, UserCog, Wifi,
  UsersRound, Shirt, TestTube, LifeBuoy, Wind, Library, Languages, Sun, MessageSquareMore, CalendarRange, Container, Volume2, Map, Factory, BadgeDollarSign, Sprout, Eye, HandHeart, Mountain, BookCopy, CloudOff, Dumbbell, Plug, ArrowUpDown,
  ListRestart, Cpu, GanttChart, BarChart2, FileEdit, CheckCircle, ParkingSquare, FileStack, Bike, FileQuestion, TrendingDown, Server, BellRing, Camera, Scissors, Cog, GitMerge, Baby, BarChart, Code, Footprints, Train, Droplets, Snowflake, Battery, ClipboardSignature, NotebookPen, DatabaseZap, CircleDollarSign, ShipWheel, SunDim, Accessibility, Home, FolderSearch, Link2, CloudSun, Hourglass, PawPrint, Swords, UserX, HeartHandshake, ScanEye,
  type LucideIcon
} from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { AIAssistantPanel } from './AIAssistantPanel';
import MobileBottomNav from './layout/MobileBottomNav';
import { GlobalSearch } from './GlobalSearch';
import { useSSE } from '../hooks/useSSE';
import { useToast } from './Toast';
import { getEnabledPrograms } from '../config/programs';
import { useProducts, type Product } from '../hooks/useProducts';
import { useYearContext } from '../contexts/YearContext';
import { ErrorBoundary } from './ErrorBoundary';

// Map DB icon string → Lucide component
const ICON_MAP: Record<string, LucideIcon> = {
  Users: Users2, Banknote, GraduationCap, Building2, Shield, BookOpen, Bus, Compass,
  TrendingUp, Leaf, Calculator, Heart, FolderKanban, Map: Compass,
  Gift, DollarSign, HardHat, Scale, Briefcase, UtensilsCrossed, Rocket, Megaphone, FolderHeart,
};

// Map DB hex colors → named Tailwind colors
const HEX_TO_COLOR: Record<string, string> = {
  '#6366F1': 'indigo', '#EA580C': 'orange', '#0D9488': 'teal',
  '#7C3AED': 'violet', '#0891B2': 'cyan', '#ec4899': 'pink',
  '#f97316': 'orange',
  '#f59e0b': 'amber',
};

// Products with dedicated sidebar sections — exclude from dynamic "Usługi CRM"
const EXCLUDED_FROM_PRODUCTS = new Set([
  'turnusy', 'szkolenia', 'dotacje_rozwojowe', 'granty_fpn', 'hutnik_biznes',
  'pup_grant', 'wsparcie_na_starcie', 'wycieczki', 'pozyczki_rozwojowe', 'kfs',
]);

export interface NavItem { path: string; icon: LucideIcon; label: string; color?: string; type?: 'link' | 'divider'; href?: string; }

// Static fallback — used until DB products load
const STATIC_PROGRAM_ITEMS: NavItem[] = getEnabledPrograms().map(p => ({
  path: p.routeBase,
  icon: p.icon,
  label: p.shortLabel,
  color: p.color,
}));

function buildProgramItems(dbProducts: Product[]): NavItem[] {
  const programFallback = getEnabledPrograms();

  if (!dbProducts || dbProducts.length === 0) {
    return STATIC_PROGRAM_ITEMS.filter(item => {
      const prog = programFallback.find(p => p.routeBase === item.path);
      return prog && !EXCLUDED_FROM_PRODUCTS.has(prog.serviceType);
    });
  }

  const items: NavItem[] = [];

  for (const prod of dbProducts) {
    if (!prod.is_active || EXCLUDED_FROM_PRODUCTS.has(prod.code)) continue;

    const fallback = programFallback.find(p => p.serviceType === prod.code);
    const routeBase = prod.crm_route_base?.startsWith('/') ? prod.crm_route_base : ('/' + (prod.crm_route_base || ''));
    const route = routeBase && routeBase !== '/' ? routeBase : fallback?.routeBase;
    if (!route) continue;

    const iconComponent = (prod.icon && ICON_MAP[prod.icon]) || fallback?.icon || BookOpen;
    const namedColor = fallback?.color || (prod.color && HEX_TO_COLOR[prod.color]) || 'blue';

    items.push({ path: route, icon: iconComponent, label: prod.short_name || prod.name, color: namedColor });
  }

  for (const prog of programFallback) {
    if (EXCLUDED_FROM_PRODUCTS.has(prog.serviceType)) continue;
    if (!items.some(i => i.path === prog.routeBase)) {
      items.push({ path: prog.routeBase, icon: prog.icon, label: prog.shortLabel, color: prog.color });
    }
  }

  return items;
}

// Sub-group divider (rendered as a small label, not a link)
const divider = (label: string): NavItem => ({
  path: `__div_${label}`, icon: ChevronRight, label, type: 'divider',
});

// ══════════════════════════════════════════════════════════════════════════
// 1. DASHBOARD
// ══════════════════════════════════════════════════════════════════════════
const DASHBOARD_ITEMS: NavItem[] = [
  { path: '/dashboard',       icon: LayoutDashboard, label: 'Dashboard',            color: 'blue' },
  { path: '/cockpit',         icon: Crown,           label: 'Executive Dashboard',  color: 'amber' },
  { path: '/pulpit-fundacji', icon: Landmark,        label: 'Pulpit Fundacji',      color: 'amber' },
  { path: '/briefing',        icon: Compass,         label: 'Mój Dzień',            color: 'cyan' },
  { path: '/tasks',           icon: ClipboardList,   label: 'Zadania',              color: 'amber' },
  { path: '/task-board',      icon: Kanban,          label: 'Tablica zadań',        color: 'violet' },
  { path: '/sprints',         icon: Rocket,          label: 'Sprint Board',         color: 'emerald' },
  { path: '/roadmap',         icon: GanttChart,      label: 'Roadmapa produktu',    color: 'indigo' },
  { path: '/project-timeline', icon: GanttChart,     label: 'Oś czasu projektów',   color: 'cyan' },
  { path: '/grant-monitor',   icon: Target,          label: 'Monitor Wniosków',     color: 'indigo' },
  { path: '/daily-digest',    icon: Clock,           label: 'Raport dzienny',       color: 'sky' },
  { path: '/kalendarz',       icon: CalendarDays,    label: 'Kalendarz',            color: 'blue' },
];

// ══════════════════════════════════════════════════════════════════════════
// 2. CRM
// ══════════════════════════════════════════════════════════════════════════
const CRM_ITEMS: NavItem[] = [
  divider('Dotacje PUP'),
  { path: '/crm',                  icon: FolderKanban,    label: 'Kanban wniosków',  color: 'indigo' },
  { path: '/crm/dashboard',        icon: LayoutDashboard, label: 'Dashboard PUP',    color: 'blue' },
  { path: '/crm/rozliczenia',      icon: Receipt,         label: 'Rozliczenia',      color: 'teal' },
  { path: '/crm/raporty',          icon: BarChart3,       label: 'Raporty PUP',      color: 'violet' },
  { path: '/crm/documents',        icon: FileText,        label: 'Dokumenty',        color: 'amber' },
  divider('Wsparcie na Start'),
  { path: '/wsparcie',              icon: FolderKanban,    label: 'Kanban WnS',       color: 'violet' },
  { path: '/wsparcie/dashboard',    icon: LayoutDashboard, label: 'Dashboard WnS',    color: 'blue' },
  { path: '/wsparcie/rozliczenia',  icon: Receipt,         label: 'Rozliczenia WnS',  color: 'teal' },
  { path: '/wsparcie/raporty',      icon: BarChart3,       label: 'Raporty WnS',      color: 'amber' },
  { path: '/wsparcie/documents',    icon: FileText,        label: 'Dokumenty WnS',    color: 'orange' },
  divider('Klienci'),
  { path: '/clients',           icon: Users2,        label: 'Klienci',              color: 'emerald' },
  { path: '/companies',         icon: Building2,     label: 'Firmy B2B',            color: 'indigo' },
  { path: '/client-360',        icon: UserCircle,    label: 'Klient 360',           color: 'indigo' },
  { path: '/client-timeline',   icon: Clock,         label: 'Historia klienta',     color: 'purple' },
  { path: '/segments',          icon: Users2,        label: 'Segmenty',             color: 'violet' },
  { path: '/admin/duplicates',  icon: Copy,          label: 'Duplikaty',            color: 'orange' },
  { path: '/contractors',       icon: Building2,     label: 'Kontrahenci',          color: 'slate' },
  { path: '/companies/cross-sell', icon: ArrowRightLeft, label: 'Cross-Sell',       color: 'violet' },
  { path: '/retention',         icon: RefreshCw,     label: 'Win-Back / Retencja',  color: 'amber' },
  { path: '/feedback',          icon: Heart,         label: 'Feedback klientów',    color: 'amber' },
  { path: '/competitor-tracker', icon: Crosshair,    label: 'Konkurencja',          color: 'red' },
  { path: '/referrals',         icon: Share2,        label: 'Program poleceń',       color: 'emerald' },
  { path: '/lead-nurturing',    icon: Magnet,        label: 'Lead Nurturing',       color: 'violet' },
  { path: '/subscriptions',     icon: RefreshCw,     label: 'Subskrypcje',          color: 'blue' },
  { path: '/proposals',         icon: FileText,      label: 'Generator ofert',      color: 'teal' },
  { path: '/onboarding',        icon: UserPlus,      label: 'Onboarding klienta',   color: 'emerald' },
  { path: '/testimonials',      icon: Star,          label: 'Opinie klientów',      color: 'amber' },
  { path: '/campaign-roi',      icon: TrendingUp,    label: 'ROI kampanii',         color: 'emerald' },
  { path: '/client-contracts',  icon: FileCheck,     label: 'Umowy klientów',       color: 'teal' },
  { path: '/partner-portal',    icon: Handshake,     label: 'Portal partnerski',    color: 'lime' },
  { path: '/communication-log', icon: PhoneCall,     label: 'Log komunikacji',      color: 'indigo' },
  { path: '/client-feedback-board', icon: MessageCircleHeart, label: 'Tablica opinii', color: 'rose' },
  { path: '/business-cards',   icon: ContactRound,  label: 'Wizytówki',             color: 'sky' },
];

// ══════════════════════════════════════════════════════════════════════════
// 3. PRODUKTY — dynamic products + dedicated modules
// ══════════════════════════════════════════════════════════════════════════
const PRODUCT_STATIC_ITEMS: NavItem[] = [
  { path: '/modules',          icon: Boxes,         label: 'Przeglądarka modułów', color: 'violet' },
  { path: '/service-catalog',  icon: Tag,           label: 'Katalog usług',        color: 'pink' },
];

const TURNUSY_ITEMS: NavItem[] = [
  { path: '/turnusy/dashboard',        icon: LayoutDashboard, label: 'Dashboard',       color: 'indigo' },
  { path: '/turnusy/klienci',          icon: Users2,          label: 'Klienci',         color: 'blue' },
  { path: '/turnusy/osrodki',          icon: Building2,       label: 'Ośrodki',          color: 'emerald' },
  { path: '/turnusy/organizatorzy',    icon: Handshake,       label: 'Organizatorzy',   color: 'lime' },
  { path: '/turnusy/turnusy',          icon: CalendarDays,    label: 'Turnusy',         color: 'violet' },
  { path: '/turnusy/rezerwacje',       icon: ClipboardList,   label: 'Rezerwacje',      color: 'amber' },
  { path: '/turnusy/dopasowanie',      icon: Sparkles,        label: 'Dopasowanie',     color: 'rose' },
  { path: '/turnusy/pokoje',           icon: Grid3x3,         label: 'Pokoje',          color: 'teal' },
  { path: '/turnusy/rezerwacje-tabela', icon: Table2,         label: 'Tabela rez.',     color: 'cyan' },
  { path: '/turnusy/raporty',          icon: BarChart3,       label: 'Raporty',         color: 'orange' },
  { path: '/turnusy/kampanie',         icon: Megaphone,       label: 'Kampanie',        color: 'pink' },
  { path: '/turnusy/pipeline',         icon: Handshake,       label: 'Pipeline',        color: 'teal' },
  { path: '/turnusy/revenue',          icon: BarChart3,       label: 'Revenue',         color: 'orange' },
  { path: '/turnusy/import-empatia',   icon: FileUp,          label: 'Import Empatia',  color: 'green' },
  { path: '/turnusy/wzbogacanie',      icon: Activity,        label: 'Wzbogacanie',     color: 'violet' },
  { path: '/turnusy/cms',              icon: FileEdit,        label: 'CMS / Szkice',    color: 'slate' },
];

const TRAINING_ITEMS: NavItem[] = [
  { path: '/szkolenia-modul',              icon: LayoutDashboard, label: 'Dashboard',    color: 'blue' },
  { path: '/szkolenia-modul/kalendarz',    icon: CalendarDays,    label: 'Kalendarz',    color: 'indigo' },
  { path: '/szkolenia-modul/sesje',        icon: ClipboardList,   label: 'Sesje',        color: 'emerald' },
  { path: '/szkolenia-modul/kursy',        icon: BookOpen,        label: 'Kursy',        color: 'violet' },
  { path: '/szkolenia-modul/trenerzy',     icon: User,            label: 'Trenerzy',     color: 'teal' },
  { path: '/szkolenia-modul/uczestnicy',   icon: Users2,          label: 'Uczestnicy',   color: 'amber' },
  { path: '/szkolenia-modul/firmy',        icon: Building2,       label: 'Firmy',        color: 'sky' },
  { path: '/szkolenia-modul/zasoby',       icon: HardDrive,       label: 'Zasoby',       color: 'orange' },
  { path: '/szkolenia-modul/testy',        icon: FileText,        label: 'Testy BUR',    color: 'pink' },
  { path: '/szkolenia-modul/bur',          icon: GraduationCap,   label: 'BUR',          color: 'cyan' },
  { path: '/szkolenia-modul/raporty',      icon: BarChart3,       label: 'Raporty',      color: 'rose' },
  { path: '/szkolenia-modul/gf-courses',   icon: BookOpen,        label: 'Katalog GF',   color: 'violet' },
  { path: '/szkolenia-modul/kfs',          icon: Award,           label: 'KFS 2026',     color: 'amber' },
  { path: '/kfs-dashboard',               icon: LayoutDashboard, label: 'KFS Hub',      color: 'sky' },
  { path: '/kfs-panel',                    icon: GraduationCap,   label: 'Panel KFS',    color: 'emerald' },
];

const WYCIECZKI_ITEMS: NavItem[] = [
  { path: '/wycieczki',             icon: Bus,          label: 'Kanban wycieczek', color: 'emerald' },
  { path: '/wycieczki/zestawienia', icon: BarChart3,    label: 'Zestawienia',      color: 'cyan' },
  { path: '/wycieczki/terminy',     icon: CalendarDays, label: 'Terminy',          color: 'blue' },
  { path: '/wycieczki/wplaty',      icon: Banknote,     label: 'Wpłaty ZDH',       color: 'emerald' },
  { path: '/wycieczki/vouchery',    icon: Gift,         label: 'Vouchery',         color: 'amber' },
];

const POZYCZKI_ITEMS: NavItem[] = [
  { path: '/pozyczki-rozwojowe',            icon: Banknote,   label: 'Kanban pożyczek', color: 'teal' },
  { path: '/pozyczki-rozwojowe/raporty',    icon: BarChart3,  label: 'Raporty',         color: 'violet' },
  { path: '/pozyczki-rozwojowe/kalkulator', icon: Calculator, label: 'Kalkulator',      color: 'amber' },
];

const SKLEP_ITEMS: NavItem[] = [
  { path: '/sklep',  icon: ShoppingCart, label: 'Sklep Nowohucki', color: 'emerald' },
  { path: '/since',  icon: ShoppingBag,  label: 'SINCE 1950',      color: 'green' },
];

const B2B_ITEMS: NavItem[] = [
  { path: '/hutnik-crm',  icon: Briefcase,  label: 'Hutnik CRM',     color: 'lime' },
  { path: '/partners',    icon: Handshake,  label: 'Partnerzy KB',   color: 'teal' },
  { path: '/angels',      icon: Award,      label: 'Anioły Biznesu', color: 'amber' },
  { path: '/pes-support', icon: HandHeart,  label: 'Wsparcie PES',   color: 'rose' },
];

// ══════════════════════════════════════════════════════════════════════════
// 4. FINANSE
// ══════════════════════════════════════════════════════════════════════════
const FINANSE_ITEMS: NavItem[] = [
  { path: '/invoices',        icon: Receipt,    label: 'Faktury',              color: 'emerald' },
  { path: '/finance',         icon: DollarSign, label: 'Dashboard finansowy',  color: 'emerald' },
  { path: '/commissions',     icon: Crown,      label: 'Prowizje',            color: 'amber' },
  { path: '/cost-tracker',    icon: Banknote,   label: 'Koszty',              color: 'rose' },
  { path: '/project-budget',  icon: Wallet,     label: 'Budżety projektów',    color: 'green' },
  { path: '/roi',             icon: TrendingUp, label: 'Kalkulator ROI',      color: 'emerald' },
  { path: '/ksef',            icon: Shield,     label: 'KSeF e-Faktury',      color: 'blue' },
  { path: '/expense-claims',  icon: CreditCard, label: 'Wydatki pracowników', color: 'orange' },
  { path: '/budget-planner', icon: Calculator,  label: 'Planowanie budżetu',   color: 'indigo' },
  { path: '/payments',        icon: CreditCard, label: 'Płatności PayU',       color: 'green' },
  { path: '/procurement',    icon: Package,    label: 'Zamówienia',          color: 'indigo' },
  { path: '/subscriptions', icon: RefreshCw,  label: 'Subskrypcje',         color: 'blue' },
  { path: '/clm',           icon: FileCheck,  label: 'Cykl życia umów',      color: 'teal' },
  { path: '/cost-centers',  icon: Landmark,   label: 'Centra kosztów',       color: 'purple' },
  { path: '/price-list',    icon: Tag,        label: 'Cennik',               color: 'amber' },
];

// ══════════════════════════════════════════════════════════════════════════
// 5. DOTACJE
// ══════════════════════════════════════════════════════════════════════════
const DOTACJE_ITEMS: NavItem[] = [
  divider('Dotacje Rozwojowe'),
  { path: '/dotacje-rozwojowe',                icon: FolderKanban, label: 'Kanban dotacji',   color: 'indigo' },
  { path: '/dotacje-rozwojowe/nabory',         icon: CalendarDays, label: 'Baza naborów',      color: 'purple' },
  { path: '/dotacje-rozwojowe/matching',       icon: Sparkles,     label: 'Matching AI',      color: 'violet' },
  { path: '/dotacje-rozwojowe/rozliczenia',    icon: Receipt,      label: 'Rozliczenia',      color: 'teal' },
  { path: '/dotacje-rozwojowe/operatorzy',     icon: Building2,    label: 'Operatorzy',       color: 'indigo' },
  { path: '/dotacje-rozwojowe/mapa',           icon: Compass,      label: 'Mapa Polski',      color: 'blue' },
  { path: '/dotacje-rozwojowe/scrape-monitor', icon: Activity,     label: 'Scrape Monitor',   color: 'emerald' },
  divider('Granty FPN'),
  { path: '/granty-fpn',        icon: FolderKanban, label: 'Kanban projektów', color: 'amber' },
  { path: '/granty-fpn/nowy',   icon: Plus,         label: 'Nowy projekt',     color: 'green' },
  { path: '/granty-fpn/wiedza', icon: BookOpen,      label: 'Baza wiedzy',      color: 'blue' },
  divider('Narzędzia dotacyjne'),
  { path: '/portfel-dotacji',   icon: Target,       label: 'Portfel dotacji',    color: 'gold' },
  { path: '/grant-calculator',  icon: Calculator,   label: 'Kalkulator dotacji', color: 'emerald' },
];

// ══════════════════════════════════════════════════════════════════════════
// 6. HR & ZESPÓŁ
// ══════════════════════════════════════════════════════════════════════════
const HR_ITEMS: NavItem[] = [
  { path: '/team-members',         icon: Users2,       label: 'Zespół HR',              color: 'blue' },
  { path: '/shift-scheduler',      icon: CalendarDays, label: 'Grafik zmian',           color: 'sky' },
  { path: '/time-tracking',        icon: Clock,        label: 'Śledzenie czasu',         color: 'teal' },
  { path: '/operator-performance', icon: Award,        label: 'Wydajność operatorów',    color: 'amber' },
  { path: '/meeting-notes',        icon: Calendar,     label: 'Notatki spotkań',         color: 'purple' },
  { path: '/okr',                  icon: Target,       label: 'Cele OKR',               color: 'purple' },
  { path: '/goal-tracker',         icon: Target,       label: 'Cele',                   color: 'emerald' },
  { path: '/appointments',         icon: Calendar,     label: 'Kalendarz wizyt',        color: 'cyan' },
  { path: '/learning-progress',   icon: GraduationCap, label: 'Szkolenia pracowników', color: 'violet' },
  { path: '/employee-directory', icon: Users2,        label: 'Katalog pracowników',  color: 'blue' },
  { path: '/certifications',     icon: Award,         label: 'Certyfikaty',          color: 'amber' },
  { path: '/resource-planner',   icon: Users2,        label: 'Planowanie zasobów',   color: 'sky' },
  { path: '/capacity-forecast', icon: TrendingUp,    label: 'Prognoza pojemności',   color: 'indigo' },
  { path: '/feedback-wall',     icon: Heart,         label: 'Tablica kudos',        color: 'rose' },
  { path: '/absences',          icon: CalendarOff,   label: 'Urlopy / Nieobecności', color: 'orange' },
  { path: '/travel-expenses',   icon: Plane,         label: 'Delegacje',            color: 'sky' },
  { path: '/payroll',            icon: Banknote,      label: 'Lista płac',            color: 'green' },
  { path: '/compliance-training', icon: Shield,       label: 'Szkolenia obowiązkowe', color: 'rose' },
  { path: '/onboarding-checklist', icon: ClipboardCheck, label: 'Onboarding pracownika', color: 'green' },
  { path: '/meeting-agenda',     icon: Calendar,     label: 'Agendy spotkań',        color: 'purple' },
  { path: '/standups',           icon: Coffee,       label: 'Daily Standup',        color: 'amber' },
  { path: '/training-needs',     icon: Target,       label: 'Potrzeby szkoleniowe', color: 'violet' },
  { path: '/emergency-contacts', icon: Phone,        label: 'Kontakty alarmowe',    color: 'red' },
  { path: '/team-mood',          icon: Smile,        label: 'Nastrój zespołu',       color: 'amber' },
  { path: '/benefits',           icon: Gift,         label: 'Benefity pracowników', color: 'emerald' },
  { path: '/retrospectives',     icon: RotateCcw,    label: 'Retrospektywy',        color: 'indigo' },
];

// ══════════════════════════════════════════════════════════════════════════
// 7. KOMUNIKACJA
// ══════════════════════════════════════════════════════════════════════════
const KOMUNIKACJA_ITEMS: NavItem[] = [
  { path: '/email',               icon: Mail,       label: 'Skrzynka email',     color: 'blue' },
  { path: '/mailing',             icon: Send,       label: 'Kampanie email',     color: 'blue' },
  { path: '/email-templates',     icon: Mail,       label: 'Szablony email',     color: 'slate' },
  { path: '/wycieczki-email',     icon: Plane,      label: 'Email Wycieczki',    color: 'cyan' },
  { path: '/email-automation',    icon: Zap,        label: 'Email Automation',   color: 'amber' },
  { path: '/newsletter-digest',   icon: Sparkles,   label: 'Newsletter AI',      color: 'purple' },
  { path: '/sms',                 icon: Phone,      label: 'SMS',                color: 'emerald' },
  { path: '/sms-gateway',         icon: Phone,      label: 'SMS Gateway',        color: 'teal' },
  { path: '/notifications',       icon: Megaphone,  label: 'Push Powiadomienia', color: 'rose' },
  { path: '/notification-center', icon: Bell,       label: 'Centrum Powiadomień', color: 'violet' },
  { path: '/social-media',        icon: Megaphone,  label: 'Social Media',       color: 'pink' },
  { path: '/edoreczenia',         icon: Mail,       label: 'e-Doręczenia',        color: 'teal' },
  { path: '/contest-admin',       icon: Trophy,     label: 'Konkurs',            color: 'amber' },
  { path: '/mail-merge',          icon: FileText,   label: 'Mail Merge',         color: 'sky' },
  { path: '/communication-log',  icon: PhoneCall,  label: 'Log komunikacji',    color: 'indigo' },
  { path: '/template-library',   icon: LayoutTemplate, label: 'Biblioteka szablonów', color: 'violet' },
  { path: '/content-calendar',  icon: CalendarDays, label: 'Kalendarz treści',     color: 'pink' },
];

// ══════════════════════════════════════════════════════════════════════════
// 8. DOKUMENTY
// ══════════════════════════════════════════════════════════════════════════
const DOKUMENTY_ITEMS: NavItem[] = [
  { path: '/wolontariat', icon: HandHeart, label: 'Wolontariat (ewidencja)', color: 'rose' },
  { path: '/zaswiadczenia-wolontariat', icon: FileSignature, label: 'Zaświadczenia (wolontariat)', color: 'emerald' },
  { path: '/document-templates', icon: FileText,      label: 'Szablony dokumentów',  color: 'teal' },
  { path: '/smart-documents',    icon: FileText,      label: 'Smart Docs AI',        color: 'blue' },
  { path: '/document-workflow',  icon: GitBranch,     label: 'Workflow dokumentów',   color: 'indigo' },
  { path: '/ocr-documents',     icon: FileSearch,    label: 'OCR Skaner',           color: 'cyan' },
  { path: '/e-signature',       icon: FileSignature, label: 'e-Podpisy',            color: 'purple' },
  { path: '/print-queue',       icon: Printer,       label: 'Kolejka druku',        color: 'indigo' },
  { path: '/export-center',     icon: FileUp,        label: 'Eksport danych',       color: 'slate' },
  { path: '/custom-forms',      icon: FileText,      label: 'Formularze',           color: 'indigo' },
  { path: '/documents-manager', icon: FolderOpen,   label: 'Menadżer dokumentów',  color: 'amber' },
  { path: '/document-archive', icon: Archive,       label: 'Archiwum dokumentów',  color: 'slate' },
  { path: '/google-drive',     icon: HardDrive,     label: 'Google Drive',         color: 'blue' },
];

// ══════════════════════════════════════════════════════════════════════════
// 9. AUTOMATYZACJA
// ══════════════════════════════════════════════════════════════════════════
const AUTOMATYZACJA_ITEMS: NavItem[] = [
  { path: '/workflow',         icon: Zap,        label: 'Workflow Builder',     color: 'purple' },
  { path: '/bulk-operations',  icon: Zap,        label: 'Operacje masowe',     color: 'amber' },
  { path: '/integrations',     icon: Globe,      label: 'Integracje',          color: 'sky' },
  { path: '/google-integrations', icon: Globe,   label: 'Google Integracje',   color: 'blue' },
  { path: '/ai-agent',         icon: Sparkles,   label: 'Agent AI',            color: 'violet' },
  { path: '/ai-modules',       icon: Rocket,     label: 'Moduły AI',            color: 'violet' },
  { path: '/ai-scoring',       icon: Sparkles,   label: 'AI Scoring',          color: 'indigo' },
];

// ══════════════════════════════════════════════════════════════════════════
// 10. ANALITYKA
// ══════════════════════════════════════════════════════════════════════════
const ANALITYKA_ITEMS: NavItem[] = [
  { path: '/reports',            icon: BarChart3,     label: 'Raporty',             color: 'violet' },
  { path: '/weekly-reports',     icon: BarChart3,     label: 'Raporty AI',          color: 'indigo' },
  { path: '/report-generator',   icon: FileBarChart2, label: 'Generator raportów',   color: 'blue' },
  { path: '/analytics',         icon: BarChart3,     label: 'Analytics',            color: 'indigo' },
  { path: '/pipeline-forecast', icon: TrendingUp,    label: 'Pipeline Forecast',   color: 'indigo' },
  { path: '/heatmap',           icon: Grid3x3,       label: 'Mapa cieplna',        color: 'orange' },
  { path: '/data-quality',      icon: Database,      label: 'Jakość danych',        color: 'cyan' },
  { path: '/activity-log',      icon: Activity,      label: 'Log aktywności',       color: 'slate' },
  { path: '/bi-dashboard',     icon: BarChart3,     label: 'Business Intelligence', color: 'emerald' },
  { path: '/client-scoring',   icon: Star,          label: 'Scoring klientów',     color: 'amber' },
  { path: '/customer-journey', icon: Route,         label: 'Mapa podróży klienta',  color: 'purple' },
  { path: '/api-analytics',   icon: Gauge,         label: 'API Analytics',        color: 'sky' },
  { path: '/knowledge-graph', icon: Network,       label: 'Graf wiedzy',          color: 'violet' },
  { path: '/revenue-forecast', icon: TrendingUp,   label: 'Prognoza przychodów',   color: 'emerald' },
  { path: '/nps',             icon: ThumbsUp,     label: 'NPS Tracker',          color: 'green' },
  { path: '/csat',            icon: Star,          label: 'CSAT Satysfakcja',     color: 'amber' },
  { path: '/client-health',   icon: HeartPulse,    label: 'Health Score klientów', color: 'rose' },
  { path: '/vendor-scorecard', icon: Star,        label: 'Scorecard dostawców',  color: 'indigo' },
  { path: '/kpi',              icon: Gauge,       label: 'KPI Dashboard',        color: 'blue' },
  { path: '/satisfaction',     icon: ThumbsUp,    label: 'Indeks satysfakcji',   color: 'green' },
  { path: '/donations-tracker', icon: Heart,      label: 'Darowizny',            color: 'rose' },
  { path: '/fundraising',       icon: HandCoins,   label: 'Fundraising',          color: 'emerald' },
];

// ══════════════════════════════════════════════════════════════════════════
// 11. ADMINISTRACJA
// ══════════════════════════════════════════════════════════════════════════
const ADMINISTRACJA_ITEMS: NavItem[] = [
  { path: '/wiki',                     icon: BookOpen,     label: 'Wiki wewnętrzne',    color: 'cyan' },
  { path: '/team-kb',                  icon: BookOpen,     label: 'Baza wiedzy zespołu', color: 'teal' },
  { path: '/crm/knowledge-base',       icon: BookOpen,     label: 'Baza wiedzy KB',     color: 'purple' },
  { path: '/rodo',                     icon: Scale,        label: 'RODO Compliance',    color: 'emerald' },
  { path: '/compliance',               icon: ClipboardList, label: 'Compliance',        color: 'amber' },
  { path: '/risk-register',            icon: Shield,       label: 'Rejestr ryzyk',      color: 'rose' },
  { path: '/helpdesk',                 icon: Headphones,   label: 'Helpdesk',           color: 'sky' },
  { path: '/contract-registry',        icon: FileText,     label: 'Rejestr umów',        color: 'indigo' },
  { path: '/assets',                   icon: HardDrive,    label: 'Zasoby / Sprzęt',    color: 'slate' },
  { path: '/office-inventory',         icon: Archive,      label: 'Inwentarz biurowy',  color: 'slate' },
  { path: '/saas-inventory',           icon: Laptop,       label: 'Inwentarz SaaS',     color: 'blue' },
  { path: '/vendors',                  icon: Building,     label: 'Dostawcy',           color: 'indigo' },
  divider('Strony WWW'),
  { path: '/websites',                 icon: Globe,        label: 'Strony klientów',     color: 'sky' },
  { path: '/www-strony-klientow',      icon: Server,       label: 'Infrastruktura WWW', color: 'purple' },
  { path: '/hosting-tracker',          icon: Server,       label: 'Hosting Tracker',    color: 'indigo' },
  { path: '/strony-wsparcie',          icon: LifeBuoy,     label: 'Wsparcie stron',     color: 'teal' },
  { path: '/migration',               icon: ArrowRightLeft, label: 'Migracja',         color: 'orange' },
  { path: '/cloudflare-dns',           icon: Cloud,        label: 'Cloudflare DNS',     color: 'orange' },
  divider('System'),
  { path: '/monitoring',               icon: Activity,     label: 'Monitoring',         color: 'rose' },
  { path: '/sla-monitor',              icon: Shield,       label: 'SLA Monitor',        color: 'rose' },
  { path: '/sla-dashboard',            icon: Gauge,        label: 'SLA Dashboard',      color: 'emerald' },
  { path: '/admin/google-workspace',   icon: HardDrive,    label: 'Konta (G+M365)',     color: 'sky' },
  { path: '/admin/cms',                icon: Settings,     label: 'CMS',                color: 'slate' },
  { path: '/admin/panel',              icon: Shield,       label: 'Admin',              color: 'slate' },
  { path: '/import',                   icon: Upload,       label: 'Import',             color: 'slate' },
  { path: '/users',                    icon: Users2,       label: 'Użytkownicy',         color: 'blue' },
  { path: '/audit',                    icon: Activity,     label: 'Audyt',              color: 'slate' },
  { path: '/webhooks',                 icon: Webhook,      label: 'Webhooks',           color: 'slate' },
  { path: '/industries',               icon: Tag,          label: 'Branże',              color: 'slate' },
  { path: '/backups',                  icon: HardDrive,    label: 'Backupy',            color: 'slate' },
  { path: '/cron-jobs',                icon: Clock,        label: 'Zadania Cron',       color: 'slate' },
  { path: '/announcements',            icon: Bell,         label: 'Ogłoszenia',          color: 'rose' },
  { path: '/changelog',                icon: FileText,     label: 'Changelog',          color: 'slate' },
  { path: '/internal-notifications',   icon: Bell,         label: 'Powiadomienia wew.', color: 'violet' },
  { path: '/tags-manager',             icon: Tag,          label: 'Menadżer tagów',      color: 'pink' },
  { path: '/visitors',                icon: DoorOpen,     label: 'Odwiedzający',        color: 'cyan' },
  { path: '/policies',                icon: ScrollText,   label: 'Polityki',            color: 'amber' },
  { path: '/incidents',               icon: AlertTriangle, label: 'Incydenty',           color: 'red' },
  { path: '/change-requests',        icon: GitPullRequest, label: 'Wnioski o zmianę',   color: 'orange' },
  { path: '/risk-assessment',        icon: Shield,       label: 'Ocena ryzyka',        color: 'rose' },
  { path: '/qa',                     icon: CheckCircle,  label: 'Kontrola jakości',     color: 'emerald' },
  { path: '/events',                 icon: CalendarCheck, label: 'Wydarzenia',          color: 'violet' },
  { path: '/meeting-rooms',          icon: DoorOpen,     label: 'Sale konferencyjne',  color: 'sky' },
  { path: '/surveys',                icon: ClipboardList, label: 'Ankiety',             color: 'indigo' },
  // Raporty dla klientów i listy kontrolne — druga strona modułu `rap_*`,
  // którego pierwsza strona żyje w panelu klienta (docs/RAPORTY-I-LISTY-KONTROLNE-MASTERPROMPT.md).
  { path: '/raporty',                icon: ClipboardList, label: 'Raporty klientów',    color: 'amber' },
  { path: '/doc-scanner',            icon: FileSearch,   label: 'Skaner dokumentów',    color: 'cyan' },
  { path: '/fleet',                  icon: Car,          label: 'Flota pojazdów',       color: 'slate' },
  { path: '/tenants',                icon: Building2,    label: 'Organizacje',         color: 'indigo' },
  { path: '/notification-rules',     icon: BellRing,     label: 'Reguły powiadomień',   color: 'violet' },
  { path: '/api-docs',                icon: Code,         label: 'API Docs',            color: 'blue' },
  { path: '/inventory',              icon: Boxes,        label: 'Magazyn',             color: 'teal' },
  { path: '/kb',                     icon: BookOpen,     label: 'Baza wiedzy v2',      color: 'indigo' },
  { path: '/brand-assets',          icon: Palette,      label: 'Zasoby marki',        color: 'pink' },
  { path: '/product-catalog',      icon: Boxes,        label: 'Katalog produktów',    color: 'emerald' },
  { path: '/ideas',                icon: Lightbulb,    label: 'Tablica pomysłów',      color: 'amber' },
  { path: '/quizzes',              icon: GraduationCap, label: 'Quizy wiedzy',         color: 'violet' },
  { path: '/whiteboards',          icon: PenTool,      label: 'Tablice',              color: 'sky' },
  { path: '/visitor-log',          icon: DoorOpen,     label: 'Rejestr gości',         color: 'cyan' },
  { path: '/equipment-maintenance', icon: Wrench,      label: 'Serwis sprzętu',       color: 'orange' },
  { path: '/release-notes',        icon: FileText,     label: 'Wersje systemu',       color: 'slate' },
  { path: '/service-desk',         icon: Headphones,   label: 'Service Desk',         color: 'sky' },
  { path: '/workspace-booking',    icon: Armchair,     label: 'Rezerwacja biurek',    color: 'teal' },
  { path: '/knowledge-faq',        icon: BookOpen,     label: 'FAQ',                  color: 'indigo' },
  { path: '/sop',                  icon: ListChecks,   label: 'Procedury SOP',        color: 'emerald' },
  { path: '/portal-config',        icon: Settings2,    label: 'Konfiguracja portalu', color: 'slate' },
  { path: '/data-import',          icon: Upload,       label: 'Import danych',        color: 'cyan' },
  { path: '/dashboard-builder',    icon: LayoutGrid,   label: 'Kreator dashboardów',   color: 'violet' },
  { path: '/access-control',       icon: Key,          label: 'Kontrola dostępu',      color: 'rose' },
  { path: '/system-health',        icon: HeartPulse,   label: 'Zdrowie systemu',      color: 'green' },
  { path: '/deliveries',           icon: Truck,        label: 'Przesyłki',            color: 'orange' },
  { path: '/asset-lifecycle',      icon: HardDrive,    label: 'Cykl życia zasobów',    color: 'slate' },
  { path: '/approvals',            icon: CheckCircle,  label: 'Akceptacje',           color: 'emerald' },
  { path: '/mailing-lists',        icon: Mail,         label: 'Listy mailingowe',     color: 'blue' },
  { path: '/legal-cases',          icon: Gavel,        label: 'Sprawy prawne',        color: 'rose' },
  { path: '/energy',               icon: Fuel,         label: 'Monitor energii',      color: 'amber' },
  { path: '/milestones',           icon: Flag,         label: 'Kamienie milowe',      color: 'indigo' },
  { path: '/parking',              icon: ParkingCircle, label: 'Parking',             color: 'slate' },
  { path: '/office-supplies',      icon: Package,      label: 'Artykuły biurowe',     color: 'teal' },
  { path: '/print-labels',         icon: Stamp,        label: 'Etykiety',             color: 'pink' },
  { path: '/weather',              icon: Sun,          label: 'Pogoda',               color: 'amber' },
  { path: '/bookshelf',            icon: BookOpen,     label: 'Biblioteczka biurowa', color: 'indigo' },
  { path: '/server-inventory',     icon: Server,       label: 'Inwentarz serwerów',    color: 'sky' },
  { path: '/password-vault',       icon: Lock,         label: 'Sejf haseł',            color: 'rose' },
  { path: '/visitor-management',  icon: DoorOpen,     label: 'Zarządzanie gośćmi',    color: 'teal' },
  { path: '/business-intelligence', icon: BarChart3,  label: 'Business Intelligence', color: 'emerald' },
  { path: '/competitor-analysis', icon: Target,       label: 'Analiza konkurencji',   color: 'orange' },
  { path: '/churn-predictor',    icon: AlertTriangle, label: 'Predykcja churnu',      color: 'red' },
  { path: '/campaign-manager',   icon: Megaphone,    label: 'Kampanie marketing',    color: 'pink' },
  { path: '/quote-builder',      icon: FileText,     label: 'Kreator ofert',         color: 'blue' },
  { path: '/deal-pipeline',      icon: TrendingUp,   label: 'Pipeline sprzedaży',    color: 'emerald' },
  { path: '/meeting-scheduler',  icon: Calendar,     label: 'Planowanie spotkań',    color: 'purple' },
  { path: '/invoice-generator',  icon: Receipt,      label: 'Generator faktur',      color: 'green' },
  { path: '/feedback-collection', icon: MessageSquare, label: 'Zbieranie opinii',    color: 'amber' },
  { path: '/notification-hub',   icon: Bell,         label: 'Centrum powiadomień',   color: 'violet' },
];

// ══════════════════════════════════════════════════════════════════════════
// EXTERNAL — standalone landing pages
// ══════════════════════════════════════════════════════════════════════════
interface ExternalItem { href: string; icon: LucideIcon; label: string; color: string; }
const EXTERNAL_ITEMS: ExternalItem[] = [
  { href: 'https://kursy.fundacjanh.org/admin', icon: GraduationCap, label: 'Kursy Online', color: 'indigo' },
  { href: 'https://bohaterowie.fundacjanh.org', icon: Heart, label: 'Bohaterowie NH', color: 'rose' },
  { href: 'https://smtrener.krakow.business', icon: Activity, label: 'SM Trener', color: 'teal' },
  { href: 'https://autoestetyka.krakow.business/admin', icon: Car, label: 'Auto Estetyka', color: 'amber' },
  { href: 'https://instytut.krakow.business', icon: Building2, label: 'Instytut KB', color: 'sky' },
  { href: 'https://smartbeauty.krakow.business', icon: Sparkles, label: 'Smart Beauty', color: 'pink' },
  { href: 'https://strategy.krakow.business', icon: BookOpen, label: 'Strategy LMS', color: 'purple' },
  { href: 'https://adamczyk.krakow.business', icon: User, label: 'Adamczyk Academy', color: 'amber' },
  { href: 'https://szkolimy.org', icon: GraduationCap, label: 'Szkolimy.org', color: 'green' },
  { href: 'https://ai.krakow.business', icon: Sparkles, label: 'AI Hub', color: 'violet' },
  { href: 'https://tenia.krakow.business', icon: Building2, label: 'Tenia Kraków', color: 'cyan' },
  { href: 'https://lot.fundacjanh.org', icon: Compass, label: 'LOT Nowa Huta', color: 'orange' },
];

// ══════════════════════════════════════════════════════════════════════════
// COLOR_MAP — active item uses gold (#d4a017) bg
// ══════════════════════════════════════════════════════════════════════════
const COLOR_MAP: Record<string, { icon: string; active: string }> = {
  blue:    { icon: 'text-blue-400',    active: 'bg-[#d4a017]' },
  emerald: { icon: 'text-emerald-400', active: 'bg-[#d4a017]' },
  amber:   { icon: 'text-amber-400',   active: 'bg-[#d4a017]' },
  violet:  { icon: 'text-violet-400',  active: 'bg-[#d4a017]' },
  orange:  { icon: 'text-orange-400',  active: 'bg-[#d4a017]' },
  slate:   { icon: 'text-slate-400',   active: 'bg-[#d4a017]' },
  indigo:  { icon: 'text-indigo-400',  active: 'bg-[#d4a017]' },
  teal:    { icon: 'text-teal-400',    active: 'bg-[#d4a017]' },
  cyan:    { icon: 'text-cyan-400',    active: 'bg-[#d4a017]' },
  green:   { icon: 'text-green-400',   active: 'bg-[#d4a017]' },
  rose:    { icon: 'text-rose-400',    active: 'bg-[#d4a017]' },
  lime:    { icon: 'text-lime-400',    active: 'bg-[#d4a017]' },
  pink:    { icon: 'text-pink-400',    active: 'bg-[#d4a017]' },
  sky:     { icon: 'text-sky-400',     active: 'bg-[#d4a017]' },
  fuchsia: { icon: 'text-fuchsia-400', active: 'bg-[#d4a017]' },
  purple:  { icon: 'text-purple-400',  active: 'bg-[#d4a017]' },
};

interface NavSection { title: string; key: string; items: NavItem[]; defaultOpen: boolean }

// Reorganizacja IA 2026-06-11: 18 sekcji → 7 grup top-level (audyt menu, workflow IA)
const SECTION_DEFAULTS: Record<string, boolean> = {
  pulpit: true,
  dotacje: true,
  produkty: false,
  klienci: false,
  operacje: false,
  ai: false,
  administracja: false,
  external: false,
};

function loadSectionState(): Record<string, boolean> {
  // Merge z defaults i odfiltruj nieznane (stare) klucze — po reorganizacji
  // localStorage może trzymać klucze starych 18 sekcji (crm/turnusy/hr...).
  try {
    const saved = localStorage.getItem('crm_sidebar_sections');
    if (saved) {
      const parsed = JSON.parse(saved) as Record<string, boolean>;
      const merged = { ...SECTION_DEFAULTS };
      for (const k of Object.keys(SECTION_DEFAULTS)) {
        if (typeof parsed[k] === 'boolean') merged[k] = parsed[k];
      }
      return merged;
    }
  } catch { /* ignore */ }
  return { ...SECTION_DEFAULTS };
}

function buildNavSections(serviceItems: NavItem[]): NavSection[] {
  // CRM_ITEMS miesza PUP/WnS (dotacje) + klientów/sprzedaż — rozdziel na granicy dividera 'Klienci'
  const kIdx = CRM_ITEMS.findIndex(i => i.type === 'divider' && i.label === 'Klienci');
  const crmDotacje = kIdx >= 0 ? CRM_ITEMS.slice(0, kIdx) : [];
  const crmKlienci = kIdx >= 0 ? CRM_ITEMS.slice(kIdx + 1) : CRM_ITEMS;
  return [
    { title: 'Pulpit',             key: 'pulpit',        items: DASHBOARD_ITEMS,                                  defaultOpen: true },
    { title: 'Dotacje i Granty',   key: 'dotacje',       items: [...crmDotacje, divider('Pożyczki'), ...POZYCZKI_ITEMS, ...DOTACJE_ITEMS], defaultOpen: true },
    { title: 'Produkty',           key: 'produkty',      items: [...PRODUCT_STATIC_ITEMS, ...serviceItems, divider('Turnusy rehabilitacyjne'), ...TURNUSY_ITEMS, divider('Szkolenia / KFS'), ...TRAINING_ITEMS, divider('Wycieczki (eNHa TRIP)'), ...WYCIECZKI_ITEMS, divider('Sklep'), ...SKLEP_ITEMS], defaultOpen: false },
    { title: 'Klienci i Sprzedaż', key: 'klienci',       items: [...crmKlienci, divider('B2B & Partnerzy'), ...B2B_ITEMS, divider('Finanse'), ...FINANSE_ITEMS], defaultOpen: false },
    { title: 'Operacje',           key: 'operacje',      items: [divider('HR & Zespół'), ...HR_ITEMS, divider('Komunikacja'), ...KOMUNIKACJA_ITEMS, divider('Dokumenty'), ...DOKUMENTY_ITEMS, divider('Analityka'), ...ANALITYKA_ITEMS], defaultOpen: false },
    { title: 'AI i Automatyzacja', key: 'ai',            items: AUTOMATYZACJA_ITEMS,                              defaultOpen: false },
    { title: 'Administracja',      key: 'administracja', items: ADMINISTRACJA_ITEMS,                              defaultOpen: false },
    { title: 'Zewnętrzne',          key: 'external',      items: [],                                               defaultOpen: false },
  ];
}

const YEAR_OPTIONS = [
  { value: new Date().getFullYear(), label: `${new Date().getFullYear()} (bieżący)` },
  { value: new Date().getFullYear() - 1, label: `${new Date().getFullYear() - 1}` },
  { value: new Date().getFullYear() - 2, label: `${new Date().getFullYear() - 2}` },
  { value: new Date().getFullYear() - 3, label: `${new Date().getFullYear() - 3}` },
  { value: new Date().getFullYear() - 4, label: `${new Date().getFullYear() - 4}` },
  { value: 0, label: 'Wszystkie lata' },
];

export const AppLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(loadSectionState);
  const { selectedYear, setSelectedYear } = useYearContext();
  const { products: dbProducts } = useProducts();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // SSE real-time notifications
  useSSE((data) => {
    toast.info(data.title + ': ' + data.message);
  });

  const user = (() => {
    try {
      const u = localStorage.getItem('grant_crm_user');
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  })();

  // Dynamic sidebar: DB products → merged with programs.ts fallback (excluding dedicated sections)
  const serviceItems = useMemo(() => buildProgramItems(dbProducts), [dbProducts]);
  const ALL_NAV_SECTIONS = useMemo(() => buildNavSections(serviceItems), [serviceItems]);

  // Per-user module access: if allowed_products is set, only show relevant sidebar sections
  const NAV_SECTIONS = useMemo(() => {
    const ap: string[] | null = user?.allowed_products;
    if (!ap || ap.length === 0 || user?.role === 'ADMIN') return ALL_NAV_SECTIONS;

    // Mapowanie produktu (allowed_products) → nowe 7 kluczy sekcji (po reorganizacji IA)
    const PRODUCT_TO_SECTIONS: Record<string, string[]> = {
      pup_grant:           ['pulpit', 'dotacje'],
      wsparcie_na_starcie: ['pulpit', 'dotacje'],
      dotacje_rozwojowe:   ['pulpit', 'dotacje'],
      granty_fpn:          ['pulpit', 'dotacje'],
      pozyczki_rozwojowe:  ['pulpit', 'dotacje'],
      kfs:                 ['pulpit', 'produkty'],
      szkolenia:           ['produkty'],
      wycieczki:           ['produkty'],
      turnusy:             ['produkty'],
      hutnik_biznes:       ['klienci'],
      sklep:               ['produkty'],
      since:               ['produkty'],
      marketing:           ['operacje'],
      czyste_powietrze:    ['produkty'],
      uslugi_ksiegowe:     ['produkty'],
      zus:                 ['produkty'],
      bhp:                 ['produkty'],
      steam_education:     ['produkty'],
      marketing_beauty:    ['produkty'],
      gastro_consulting:   ['produkty'],
    };

    const allowedKeys = new Set<string>(['pulpit']);
    for (const code of ap) {
      const sectionKeys = PRODUCT_TO_SECTIONS[code];
      if (sectionKeys) sectionKeys.forEach(k => allowedKeys.add(k));
    }

    return ALL_NAV_SECTIONS
      .filter(s => allowedKeys.has(s.key))
      // Faktury zawsze dostępne — wstrzyknij gdy grupa 'klienci' (zawiera Finanse) niewidoczna
      .concat(
        !allowedKeys.has('klienci')
          ? [{ title: 'Faktury', key: 'invoices', items: [{ path: '/invoices', icon: Receipt, label: 'Faktury' }], defaultOpen: true }]
          : []
      ) as typeof ALL_NAV_SECTIONS;
  }, [ALL_NAV_SECTIONS, user?.allowed_products, user?.role]);

  const toggleSection = (key: string) => {
    setOpenSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      safeStorage.set('crm_sidebar_sections', JSON.stringify(next));
      return next;
    });
  };

  // Auto-close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Auto-open sidebar section containing active route
  useEffect(() => {
    const section = NAV_SECTIONS.find((s: NavSection) =>
      s.items.some((item: NavItem) => item.type !== 'divider' && location.pathname.startsWith(item.path))
    );
    if (section && !openSections[section.key]) {
      setOpenSections(prev => {
        const next = { ...prev, [section.key]: true };
        safeStorage.set('crm_sidebar_sections', JSON.stringify(next));
        return next;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    safeStorage.remove('grant_crm_token');
    safeStorage.remove('grant_crm_user');
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/crm') return location.pathname === '/crm' || location.pathname.startsWith('/crm/');
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-slate-900 text-white flex items-center justify-between px-3 z-40">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          aria-label="Menu"
        >
          <Menu size={22} />
        </button>
        <span className="font-bold text-sm">GrantOS</span>
        <NotificationBell />
      </div>

      {/* Backdrop overlay (mobile) */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-gradient-to-b from-slate-900 to-slate-800 text-white flex flex-col z-50 transition-all duration-200
          ${collapsed ? 'w-16' : 'w-52'}
          -translate-x-full md:translate-x-0
          ${mobileOpen ? 'translate-x-0' : ''}
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-3 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <LayoutDashboard size={18} />
            </div>
            {!collapsed && <span className="font-bold text-sm truncate">GrantOS</span>}
          </div>
          {/* Close button on mobile */}
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1 hover:bg-slate-700 rounded transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Year selector */}
        {!collapsed ? (
          <div className="px-3 py-2 border-b border-slate-700">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              <Calendar size={12} />
              <span>Rok edycji</span>
            </div>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              aria-label="Rok edycji"
              className="w-full bg-slate-800 text-white text-sm rounded px-2 py-1.5 border border-slate-600 focus:border-blue-400 focus:outline-none"
            >
              {YEAR_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="px-2 py-2 border-b border-slate-700 flex justify-center">
            <div
              className="w-8 h-8 bg-slate-700 rounded flex items-center justify-center text-xs font-bold text-slate-300 cursor-pointer hover:bg-slate-600"
              title={`Rok: ${selectedYear || 'Wszystkie'}`}
              onClick={() => {
                const idx = YEAR_OPTIONS.findIndex(o => o.value === selectedYear);
                const next = YEAR_OPTIONS[(idx + 1) % YEAR_OPTIONS.length];
                setSelectedYear(next.value);
              }}
            >
              {selectedYear || '∞'}
            </div>
          </div>
        )}

        {/* Quick search trigger */}
        <div className="px-2 py-2 border-b border-slate-700">
          {!collapsed ? (
            <button
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 bg-slate-800/60 hover:bg-slate-700/80 hover:text-white transition-colors border border-slate-700/50"
            >
              <Search size={14} className="flex-shrink-0" />
              <span className="flex-1 text-left text-xs">Szukaj...</span>
              <kbd className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-slate-500 rounded border border-slate-600 font-mono">Ctrl+K</kbd>
            </button>
          ) : (
            <button
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))}
              className="w-full flex items-center justify-center py-2 text-slate-400 hover:text-white transition-colors"
              title="Szukaj (Ctrl+K)"
            >
              <Search size={18} />
            </button>
          )}
        </div>

        {/* Nav sections */}
        <nav className="flex-1 py-2 px-2 overflow-y-auto">
          {NAV_SECTIONS.map((section, si) => {
            const isOpen = openSections[section.key] ?? section.defaultOpen;
            const hasActiveItem = section.items.some(item => item.type !== 'divider' && isActive(item.path));

            return (
              <div key={section.key} className={si > 0 ? 'mt-1' : ''}>
                {!collapsed ? (
                  <button
                    onClick={() => toggleSection(section.key)}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                      hasActiveItem && !isOpen
                        ? 'text-[#d4a017] hover:text-[#e8b42a]'
                        : 'text-slate-300 hover:text-white'
                    }`}
                    style={{ letterSpacing: '0.05em' }}
                  >
                    <span>{section.title}</span>
                    <ChevronDown
                      size={12}
                      className={`transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
                    />
                  </button>
                ) : (
                  si > 0 && <div className="mx-2 my-2 border-t border-slate-700" />
                )}

                {(collapsed || isOpen) && (
                  <div className="space-y-0.5">
                    {section.key === 'external' ? (
                      EXTERNAL_ITEMS.map(ext => {
                        const cm = ext.color ? COLOR_MAP[ext.color] : undefined;
                        return (
                          <a
                            key={ext.href}
                            href={ext.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors text-slate-200 hover:bg-slate-700 hover:text-white"
                            title={collapsed ? ext.label : undefined}
                          >
                            <ext.icon size={18} className={`flex-shrink-0 ${cm ? cm.icon : ''}`} />
                            {!collapsed && (
                              <>
                                <span className="truncate">{ext.label}</span>
                                <ExternalLink size={12} className="ml-auto text-slate-500 flex-shrink-0" />
                              </>
                            )}
                          </a>
                        );
                      })
                    ) : (
                      section.items.map(item => {
                        // Sub-group divider (small label)
                        if (item.type === 'divider') {
                          if (collapsed) return null;
                          return (
                            <div key={item.path} className="px-3 pt-2.5 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-500">
                              {item.label}
                            </div>
                          );
                        }
                        const active = !item.href && isActive(item.path);
                        const cm = item.color ? COLOR_MAP[item.color] : undefined;
                        if (item.href) {
                          return (
                            <a
                              key={item.path}
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors text-slate-200 hover:bg-slate-700 hover:text-white"
                              title={collapsed ? item.label : undefined}
                            >
                              <item.icon size={18} className={`flex-shrink-0 ${cm ? cm.icon : ''}`} />
                              {!collapsed && (
                                <>
                                  <span className="truncate">{item.label}</span>
                                  <ExternalLink size={12} className="ml-auto text-slate-500 flex-shrink-0" />
                                </>
                              )}
                            </a>
                          );
                        }
                        return (
                          <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                              active
                                ? `${cm?.active || 'bg-blue-600'} text-white shadow-md`
                                : 'text-slate-200 hover:bg-slate-700 hover:text-white'
                            }`}
                            title={collapsed ? item.label : undefined}
                          >
                            <item.icon size={18} className={`flex-shrink-0 ${!active && cm ? cm.icon : ''}`} />
                            {!collapsed && <span className="truncate">{item.label}</span>}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Notification bell (desktop only) */}
          <div className={`hidden md:block px-3 py-2 mt-2 ${collapsed ? 'flex justify-center' : ''}`}>
            <NotificationBell />
          </div>
        </nav>

        {/* User + Logout */}
        <div className="border-t border-slate-700 p-2 space-y-1">
          {!collapsed && user && (
            <div className="px-3 py-2 text-xs text-slate-400 truncate">
              {user.firstName || user.username}
              <span className="ml-1 text-slate-500">({user.role})</span>
            </div>
          )}
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-slate-700/60 hover:text-white transition-colors"
            title={collapsed ? 'Ustawienia' : undefined}
          >
            <Settings size={20} className="flex-shrink-0" />
            {!collapsed && <span>Ustawienia</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-red-600/20 hover:text-red-300 transition-colors"
            title={collapsed ? 'Wyloguj' : undefined}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {!collapsed && <span>Wyloguj</span>}
          </button>

          {/* Collapse toggle (desktop only) */}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="hidden md:flex w-full items-center justify-center py-2 text-slate-400 hover:text-white transition-colors"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 transition-all duration-200 pt-14 md:pt-0 pb-16 md:pb-0 ${collapsed ? 'md:ml-16' : 'md:ml-52'}`}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* AI Assistant floating button */}
      {!aiPanelOpen && (
        <button
          onClick={() => setAiPanelOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
          title="AI Asystent"
        >
          <Sparkles size={20} className="group-hover:animate-pulse" />
        </button>
      )}

      {/* Mobile Bottom Nav */}
      <MobileBottomNav onMenuToggle={() => setMobileOpen(!mobileOpen)} />

      {/* AI Assistant Panel */}
      <AIAssistantPanel isOpen={aiPanelOpen} onClose={() => setAiPanelOpen(false)} />

      {/* Global Search (Ctrl+K) */}
      <GlobalSearch />
    </div>
  );
};
